// Builds the react-router route tree from the App Router file layout the UI
// was written in (src/app/**/page.tsx + layout.tsx), so a page added the
// Next.js way — a folder with a page.tsx — is routed with no table to edit:
//
//   (group)        → no path segment (a pathless route)
//   [id]           → :id
//   [...x], [[...x]] → * (the splat), exposed as params.x: string[] (nav.tsx)
//   layout.tsx     → a route whose element wraps its children's <Outlet/>
//   page.tsx       → that folder's index route (the splat route itself for a catch-all)
//
// Pure over its inputs: appRoutes.tsx feeds it import.meta.glob results; the
// test feeds it fakes.

import { Suspense, lazy, useEffect, type ComponentType, type ReactNode } from "react";
import { Outlet, useSearchParams as useRouterSearchParams, type RouteObject } from "react-router";

import type { Metadata } from "@/nav/metadata";
import { RefreshBoundary, useParams, type RouteHandle, type RouteParams } from "@/nav/navigation";

export interface PageProps {
  params: RouteParams;
  searchParams: RouteParams;
}
export interface LayoutProps {
  children: ReactNode;
  params: RouteParams;
}
export interface PageModule {
  default: ComponentType<PageProps>;
  metadata?: Metadata;
}
export interface LayoutModule {
  default: ComponentType<LayoutProps>;
}
export type Loader<M> = () => Promise<M>;

interface DirNode {
  segment: string;
  page?: Loader<PageModule>;
  layout?: Loader<LayoutModule>;
  children: Map<string, DirNode>;
}

/** "../app/(mc)/admin/page.tsx" → ["(mc)", "admin"]. */
export function dirSegments(globKey: string, file: "page.tsx" | "layout.tsx"): string[] {
  const at = globKey.indexOf("/app/");
  if (at < 0 || !globKey.endsWith(`/${file}`)) throw new Error(`[routes] not an app file: ${globKey}`);
  const inner = globKey.slice(at + "/app/".length, -file.length);
  return inner.split("/").filter(Boolean);
}

/** One App Router folder name → its react-router path piece. */
export function routeSegment(segment: string): { path: string | undefined; catchAll?: string; optional?: boolean } {
  if (/^\(.*\)$/.test(segment)) return { path: undefined };
  const optionalCatchAll = /^\[\[\.\.\.(.+)\]\]$/.exec(segment);
  if (optionalCatchAll) return { path: "*", catchAll: optionalCatchAll[1], optional: true };
  const catchAll = /^\[\.\.\.(.+)\]$/.exec(segment);
  if (catchAll) return { path: "*", catchAll: catchAll[1] };
  const dynamicSeg = /^\[(.+)\]$/.exec(segment);
  if (dynamicSeg) return { path: `:${dynamicSeg[1]}` };
  return { path: segment };
}

function searchParamsRecord(sp: URLSearchParams): RouteParams {
  const out: RouteParams = {};
  for (const key of new Set(sp.keys())) {
    const all = sp.getAll(key);
    out[key] = all.length > 1 ? all : all[0];
  }
  return out;
}

const lazyCache = new Map<Loader<unknown>, ComponentType<{ props: PageProps }>>();

function pageComponent(load: Loader<PageModule>): ComponentType<{ props: PageProps }> {
  const hit = lazyCache.get(load as Loader<unknown>);
  if (hit) return hit;
  const Comp = lazy(async () => {
    const mod = await load();
    const Page = mod.default;
    const title = mod.metadata?.title;
    function WithMeta({ props }: { props: PageProps }) {
      useEffect(() => {
        if (title) document.title = title;
      }, []);
      return <Page {...props} />;
    }
    return { default: WithMeta };
  });
  lazyCache.set(load as Loader<unknown>, Comp);
  return Comp;
}

function PageRoute({ load }: { load: Loader<PageModule> }) {
  const params = useParams();
  const [sp] = useRouterSearchParams();
  const Page = pageComponent(load);
  return (
    <RefreshBoundary>
      <Suspense fallback={null}>
        <Page props={{ params, searchParams: searchParamsRecord(sp) }} />
      </Suspense>
    </RefreshBoundary>
  );
}

const layoutCache = new Map<Loader<LayoutModule>, ComponentType<LayoutProps>>();

function LayoutRoute({ load }: { load: Loader<LayoutModule> }) {
  const params = useParams();
  let Layout = layoutCache.get(load);
  if (!Layout) {
    Layout = lazy(load);
    layoutCache.set(load, Layout);
  }
  return (
    <Suspense fallback={null}>
      <Layout params={params}>
        <Outlet />
      </Layout>
    </Suspense>
  );
}

function insert(root: DirNode, segs: string[]): DirNode {
  let node = root;
  for (const s of segs) {
    let next = node.children.get(s);
    if (!next) {
      next = { segment: s, children: new Map() };
      node.children.set(s, next);
    }
    node = next;
  }
  return node;
}

function toRoute(node: DirNode, isRoot: boolean): RouteObject {
  const seg = isRoot ? { path: undefined } : routeSegment(node.segment);
  const children: RouteObject[] = [];
  const handle: RouteHandle | undefined = seg.catchAll ? { catchAll: seg.catchAll } : undefined;
  // A catch-all folder's page IS the splat route; elsewhere the page is the index.
  const pageElement = node.page ? <PageRoute load={node.page} /> : undefined;
  if (pageElement && !seg.catchAll) children.push({ index: true, element: pageElement });
  for (const child of node.children.values()) {
    children.push(toRoute(child, false));
    // An OPTIONAL catch-all ([[...x]]) also matches its parent's bare path
    // (/sign-in, /mc). React Router ranks the exact parent above the child's
    // "*" splat, so without an index here /sign-in matched the element-less
    // "sign-in" route and rendered nothing (gypsy-dev, 2026-11-02). The page
    // is the parent's index too; a parent with its own page keeps that one.
    const cs = routeSegment(child.segment);
    if (cs.catchAll && cs.optional && child.page && !node.page) {
      children.push({ index: true, element: <PageRoute load={child.page} />, handle: { catchAll: cs.catchAll } });
    }
  }
  const route: RouteObject = {
    ...(isRoot ? { path: "/" } : seg.path === undefined ? {} : { path: seg.path }),
    ...(handle ? { handle } : {}),
  };
  if (node.layout) {
    route.element = <LayoutRoute load={node.layout} />;
    if (seg.catchAll && pageElement) children.push({ index: true, element: pageElement, handle });
  } else if (seg.catchAll && pageElement) {
    route.element = pageElement;
  }
  if (children.length > 0) route.children = children;
  return route;
}

export function buildRoutes(input: {
  pages: Record<string, Loader<PageModule>>;
  layouts: Record<string, Loader<LayoutModule>>;
  /** Rendered for any path no page matched, inside the root layout. */
  notFound: ReactNode;
  /** The root route's error boundary. */
  errorElement: ReactNode;
}): RouteObject[] {
  const root: DirNode = { segment: "", children: new Map() };
  for (const [key, load] of Object.entries(input.pages)) insert(root, dirSegments(key, "page.tsx")).page = load;
  for (const [key, load] of Object.entries(input.layouts)) insert(root, dirSegments(key, "layout.tsx")).layout = load;
  const top = toRoute(root, true);
  top.children = [...(top.children ?? []), { path: "*", element: input.notFound }];
  top.errorElement = input.errorElement;
  return [top];
}
